import { useCallback, useMemo, useState } from "react"

import { isJsonObject } from "@/types/json"
import type { RunRowLike } from "@/types/RunRow"

import type { RunResultsMetricRenderAdaptersById } from "../types"
import {
  DEFAULT_SIMILARITY_THRESHOLD,
  groupRowsByExplanationSimilarity,
} from "../utils/groupBySimilarity"

function getLlmAnswerExplanation(metricOutput: {
  result?: Record<string, unknown> | null
}): string {
  const llm = metricOutput.result?.llm
  if (!isJsonObject(llm)) {
    return ""
  }
  const value = llm.answer_explanation
  return typeof value === "string" ? value : ""
}

interface UseExplanationSimilarityReorderParams {
  metricIdFilter: string | null
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  runRows: RunRowLike[]
}

interface UseExplanationSimilarityReorderResult {
  similarityThreshold: number
  setSimilarityThreshold: (value: number) => void
  isActive: boolean
  reorder: <T extends RunRowLike>(rows: T[]) => T[]
}

/**
 * Encapsulates the threshold slider state and the row-reorder closure used by
 * the run results tables to group rows with similar explanations contiguously.
 */
export function useExplanationSimilarityReorder({
  metricIdFilter,
  metricRenderAdaptersById,
  runRows,
}: UseExplanationSimilarityReorderParams): UseExplanationSimilarityReorderResult {
  const [similarityThreshold, setSimilarityThreshold] = useState(
    DEFAULT_SIMILARITY_THRESHOLD,
  )

  const metricRecord = metricIdFilter
    ? metricRenderAdaptersById?.[metricIdFilter]
    : undefined

  const hasExplanations = useMemo(() => {
    if (!metricRecord?.id) {
      return false
    }
    return runRows.some((row) => {
      const mo = row.metric_outputs?.[metricRecord.id]
      if (!mo) {
        return false
      }
      return getLlmAnswerExplanation(mo).trim() !== ""
    })
  }, [metricRecord, runRows])

  const isActive = Boolean(metricRecord) && hasExplanations

  const reorder = useCallback(
    <T extends RunRowLike>(rows: T[]): T[] => {
      if (!metricRecord?.id || rows.length <= 1) {
        return rows
      }
      const getExplanation = (row: RunRowLike): string => {
        const mo = row.metric_outputs?.[metricRecord.id]
        if (!mo) {
          return ""
        }
        return getLlmAnswerExplanation(mo)
      }
      return groupRowsByExplanationSimilarity({
        rows,
        getExplanation,
        threshold: similarityThreshold,
      }) as T[]
    },
    [metricRecord, similarityThreshold],
  )

  return useMemo(
    () => ({ similarityThreshold, setSimilarityThreshold, isActive, reorder }),
    [similarityThreshold, isActive, reorder],
  )
}
